/**
 * Whether a player on the stage has its file. A recording can be missing or
 * refused (the object store answers 403, the network drops), and whoever
 * watches the player - the room, the console - must be able to tell that from
 * a recording that is still coming.
 */
export type MediaStatus = 'loading' | 'ready' | 'failed'

/**
 * A file that has not arrived or been decoded by now is given up on: a
 * refused request fails at once, so this only catches a request that hangs.
 * Long enough for a long recording over a venue's Wi-Fi.
 */
export const MEDIA_LOAD_TIMEOUT_MS = 30_000

export type MediaLoadState = {
	status: MediaStatus
	/** How many times the file was asked for again, by hand. */
	attempt: number
}

export type MediaLoadEvent =
	/** The player starts on a file (the first one, or another recording). */
	| 'started'
	| 'loaded'
	| 'failed'
	| 'timed-out'
	/** Someone asks for the file again. */
	| 'retry'

export const INITIAL_MEDIA_LOAD: MediaLoadState = {
	status: 'loading',
	attempt: 0,
}

/**
 * What a player's signals make of its status:
 *
 * - `loaded` wins over everything: a file that arrives after the timeout is
 *   still a file;
 * - `failed` (the player's own error, the request's rejection) ends a load
 *   and a playback alike;
 * - `timed-out` only ends a load that is still going;
 * - `retry` only does anything after a failure - asking again must not stop a
 *   recording that plays - and moves to a new attempt.
 */
export const nextMediaLoad = (
	state: MediaLoadState,
	event: MediaLoadEvent
): MediaLoadState => {
	switch (event) {
		case 'started':
			return state.status === 'loading'
				? state
				: { ...state, status: 'loading' }
		case 'loaded':
			return state.status === 'ready' ? state : { ...state, status: 'ready' }
		case 'failed':
			return state.status === 'failed' ? state : { ...state, status: 'failed' }
		case 'timed-out':
			return state.status === 'loading' ? { ...state, status: 'failed' } : state
		case 'retry':
			return state.status === 'failed'
				? { status: 'loading', attempt: state.attempt + 1 }
				: state
	}
}
